import { Form, Input, Radio, Switch } from 'antd'
import { useTranslation } from 'react-i18next'

import { Button, Trans } from '@/components'
import { ENV_KEY_PATTERN } from '@/consts/image'

interface ImageConfigFieldsProps {
  advancedOpen: boolean
  onAdvancedOpenChange: (open: boolean) => void
  minResourceOpen: boolean
  onMinResourceOpenChange: (open: boolean) => void
  showAdvanced?: boolean
}

export function ImageConfigFields({
  advancedOpen,
  onAdvancedOpenChange,
  minResourceOpen,
  onMinResourceOpenChange,
  showAdvanced = true,
}: ImageConfigFieldsProps) {
  const { t } = useTranslation()

  return (
    <>
      {showAdvanced && (
        <div className="border border-neutral-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="t-sb text-primary">
              <Trans i18nKey="customImage.importModal.advancedConfig">
                Advanced Configurations
              </Trans>
            </div>
            <Switch
              checked={advancedOpen}
              onChange={onAdvancedOpenChange}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>

          {advancedOpen && (
            <div className="mt-4">
              <div className="t-sb text-primary mb-2">
                <Trans i18nKey="customImage.importModal.envVariables">Environment Variables</Trans>
              </div>
              <Form.List name="envVars">
                {(fields, { add, remove }) => (
                  <div className="flex flex-col gap-3">
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="flex items-start gap-3">
                        <Form.Item
                          {...restField}
                          name={[name, 'key']}
                          className="!mb-0 flex-1"
                          rules={[
                            {
                              validator: (_, value: string | undefined) => {
                                if (!value?.trim()) return Promise.resolve()
                                if (ENV_KEY_PATTERN.test(value.trim())) return Promise.resolve()
                                return Promise.reject(
                                  new Error(t('customImage.importModal.validation.invalidEnvKey')),
                                )
                              },
                            },
                          ]}
                        >
                          <Input
                            className="h-11 px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                            placeholder={t('customImage.importModal.envKeyPlaceholder')}
                          />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'value']} className="!mb-0 flex-1">
                          <Input
                            className="h-11 px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                            placeholder={t('customImage.importModal.envValuePlaceholder')}
                          />
                        </Form.Item>
                        {fields.length >= 2 && (
                          <Button
                            type="default"
                            className="h-11 px-4 shrink-0"
                            onClick={() => remove(name)}
                          >
                            {t('customImage.importModal.removeBtn')}
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="default"
                      block
                      className="h-11"
                      onClick={() => add({ key: '', value: '' })}
                    >
                      {t('customImage.importModal.addEnvVariable')}
                    </Button>
                  </div>
                )}
              </Form.List>

              <div className="t-sb text-primary mb-2 mt-4">
                <Trans i18nKey="customImage.importModal.startupCommand">Startup Command</Trans>
              </div>
              <Form.Item name="startupCommand" className="!mb-4">
                <Input.TextArea
                  rows={3}
                  className="px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.startupCommandPlaceholder')}
                />
              </Form.Item>

              <div className="t-sb text-primary mb-2">
                <Trans i18nKey="customImage.importModal.disableSsh">Disable SSH Service</Trans>
              </div>
              <Form.Item name="disableSsh" className="!mb-0">
                <Radio.Group className="w-full grid grid-cols-2 gap-3">
                  <Radio
                    value={true}
                    className="flex items-center px-4 py-3 border border-gray300 rounded-lg t-sb text-primary"
                  >
                    {t('customImage.importModal.yes')}
                  </Radio>
                  <Radio
                    value={false}
                    className="flex items-center px-4 py-3 border border-gray300 rounded-lg t-sb text-primary"
                  >
                    {t('customImage.importModal.no')}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          )}
        </div>
      )}

      {/* Minimum Resources (min_resource) — optional, default off; 0 means no limit */}
      <div className="border border-neutral-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="t-sb text-primary">
            <Trans i18nKey="customImage.importModal.minResources">Minimum Resources</Trans>
          </div>
          <Switch
            checked={minResourceOpen}
            onChange={onMinResourceOpenChange}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </div>

        {minResourceOpen && (
          <div className="mt-4 flex flex-col gap-4">
            <div>
              <div className="t-sb text-primary mb-2">
                <Trans i18nKey="customImage.importModal.cpuCores">CPU Cores</Trans>
              </div>
              <Form.Item
                name="minCpus"
                className="!mb-0"
                getValueFromEvent={(e) => e.target.value.replace(/[^\d]/g, '')}
              >
                <Input
                  className="h-11 px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.cpuCoresPlaceholder')}
                  suffix={
                    <span className="t-sb text-primary">
                      {t('customImage.importModal.coresUnit')}
                    </span>
                  }
                />
              </Form.Item>
            </div>
            <div>
              <div className="t-sb text-primary mb-2">
                <Trans i18nKey="customImage.importModal.memoryRam">Memory(RAM)</Trans>
              </div>
              <Form.Item
                name="minRamMB"
                className="!mb-0"
                getValueFromEvent={(e) => e.target.value.replace(/[^\d]/g, '')}
              >
                <Input
                  className="h-11 px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.ramPlaceholder')}
                  suffix={
                    <span className="t-sb text-primary">{t('customImage.importModal.mbUnit')}</span>
                  }
                />
              </Form.Item>
            </div>
            <div>
              <div className="t-sb text-primary mb-2">
                <Trans i18nKey="customImage.importModal.systemDisk">System Disk</Trans>
              </div>
              <Form.Item
                name="minDiskGB"
                className="!mb-0"
                getValueFromEvent={(e) => e.target.value.replace(/[^\d]/g, '')}
              >
                <Input
                  className="h-11 px-4 py-3 t-sb border-neutral-400 placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.diskPlaceholder')}
                  suffix={
                    <span className="t-sb text-primary">{t('customImage.importModal.gbUnit')}</span>
                  }
                />
              </Form.Item>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
